import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Layers, Lock, Mail, Eye, EyeOff, Loader2 } from 'lucide-react';
import { track } from '@/utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' · ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * Reusable auth form (Sign In ↔ Create Account).
 * Used both on the dedicated /login screen and embedded inside the LandingPage hero.
 *
 * - When `mode` is provided, it is treated as controlled. Otherwise the card manages its own.
 * - On register success, hard-navigates to /paper-trading so AuthContext rehydrates from localStorage.
 * - On login success, calls onLogin(user) so the parent can flip auth state in-place.
 *
 * The `variant="page"` keeps the original full-screen logo + title block.
 * The `variant="embedded"` is leaner for the landing page (no big logo, smaller padding).
 */
export default function AuthCard({
  onLogin,
  onForgotPassword,
  mode: controlledMode,
  onModeChange,
  defaultMode = 'login',
  variant = 'page',
}) {
  const [internalMode, setInternalMode] = useState(defaultMode);
  const mode = controlledMode ?? internalMode;
  const setMode = (m) => {
    if (onModeChange) onModeChange(m);
    if (controlledMode === undefined) setInternalMode(m);
  };

  // Keep internal in sync if defaultMode changes (e.g., from outside on first mount)
  useEffect(() => {
    if (controlledMode === undefined) setInternalMode(defaultMode);
  }, [defaultMode, controlledMode]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const isRegister = mode === 'register';
  const isEmbedded = variant === 'embedded';

  // Confirm-password validation: only render the inline error after the user
  // has typed something into the second field (avoids scolding them mid-flow).
  const passwordsMismatch =
    isRegister && confirmPassword.length > 0 && password !== confirmPassword;
  const isFormValid =
    !isRegister ||
    (password.length >= 8 && confirmPassword.length > 0 && password === confirmPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    if (isRegister && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (isRegister && password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      // Name is no longer a UI field — backend derives it from the email prefix
      // when omitted. Users can rename themselves later from Account settings.
      const body = { email: email.trim().toLowerCase(), password };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      // Single, guarded JSON read. `.json()` can only be called ONCE on a
      // Response — if anything goes wrong reading the body (empty, non-JSON,
      // stream already consumed by a proxy/interceptor), fall back to an
      // empty object so error handling below still runs cleanly.
      let data = {};
      try {
        const raw = await response.text();
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }

      if (!response.ok) {
        // Unverified email: show a resend affordance instead of a generic error
        if (
          !isRegister &&
          response.status === 403 &&
          data?.detail?.code === 'email_not_verified'
        ) {
          const normalizedEmail = email.trim().toLowerCase();
          toast.error(data.detail.message || 'Please verify your email to continue.', {
            duration: 10000,
            action: {
              label: 'Resend link',
              onClick: async () => {
                try {
                  await fetch(`${API_URL}/api/auth/resend-verification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: normalizedEmail }),
                  });
                  toast.success('If that email exists and is unverified, we just sent a fresh link.');
                } catch {
                  toast.error('Could not resend — try again in a moment.');
                }
              },
            },
          });
          return;
        }
        throw new Error(
          formatApiErrorDetail(data?.detail) || (isRegister ? 'Sign up failed' : 'Login failed')
        );
      }

      // Register no longer returns a JWT — it asks the user to check their inbox.
      if (isRegister && data?.requires_verification) {
        track('signup_completed', { source: 'auth_card', awaiting_verification: true });
        toast.success('Check your inbox', {
          description: data.message || 'We sent a verification link to your email.',
          duration: 9000,
        });
        // Keep form state so the user can request a resend if needed.
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        return;
      }

      localStorage.setItem('tp360_token', data.access_token);
      localStorage.setItem('tp360_user', JSON.stringify(data.user));

      toast.success(`Welcome back, ${data.user.name}!`);
      // Always send users straight to Paper Trading — the core product surface.
      window.location.assign('/paper-trading');
    } catch (error) {
      console.error(`${isRegister ? 'Register' : 'Login'} error:`, error);
      toast.error(error.message || (isRegister ? 'Sign up failed' : 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      className={`w-full ${
        isEmbedded ? 'max-w-md p-6' : 'max-w-md p-8'
      } bg-[#111113]/95 backdrop-blur-xl border-white/5`}
      data-testid="auth-card"
    >
      {!isEmbedded && (
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 mb-4">
            <Layers className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-foreground">Trading Pilot</h1>
          <p className="text-primary font-bold">360</p>
          <p className="text-sm text-muted-foreground mt-2">
            {isRegister ? 'Create your free account' : 'Decision Support Dashboard'}
          </p>
        </div>
      )}

      {isEmbedded && (
        <div className="mb-5">
          <h2 className="text-xl font-bold text-foreground">
            {isRegister ? 'Create your free account' : 'Welcome back'}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {isRegister
              ? 'No approval needed · Instant access'
              : 'Sign in to your trading dashboard'}
          </p>
        </div>
      )}

      <div
        className="grid grid-cols-2 gap-1 p-1 mb-5 rounded-lg bg-white/5 border border-white/5"
        data-testid="auth-mode-toggle"
      >
        <button
          type="button"
          onClick={() => setMode('login')}
          className={`text-sm font-semibold py-2 rounded-md transition-colors ${
            !isRegister
              ? 'bg-primary text-primary-foreground shadow'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="auth-tab-login"
        >
          Sign In
        </button>
        <button
          type="button"
          onClick={() => setMode('register')}
          className={`text-sm font-semibold py-2 rounded-md transition-colors ${
            isRegister
              ? 'bg-primary text-primary-foreground shadow'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="auth-tab-register"
        >
          Create Account
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="auth-email" className="text-sm font-medium">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="pl-10 h-11 bg-white/5 border-white/10 focus:border-primary"
              data-testid={isRegister ? 'register-email' : 'login-email'}
              disabled={loading}
              autoComplete="email"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="auth-password" className="text-sm font-medium">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="auth-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? 'At least 8 characters' : 'Enter your password'}
              className="pl-10 pr-10 h-11 bg-white/5 border-white/10 focus:border-primary"
              data-testid={isRegister ? 'register-password' : 'login-password'}
              disabled={loading}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              minLength={isRegister ? 8 : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {isRegister && (
          <div className="space-y-2">
            <Label htmlFor="auth-confirm-password" className="text-sm font-medium">
              Confirm Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="auth-confirm-password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className={`pl-10 h-11 bg-white/5 border-white/10 focus:border-primary ${
                  passwordsMismatch ? 'border-destructive focus:border-destructive' : ''
                }`}
                data-testid="register-confirm-password"
                disabled={loading}
                autoComplete="new-password"
                aria-invalid={passwordsMismatch}
                aria-describedby={passwordsMismatch ? 'confirm-password-error' : undefined}
              />
            </div>
            {passwordsMismatch && (
              <p
                id="confirm-password-error"
                className="text-xs text-destructive"
                data-testid="confirm-password-error"
              >
                Passwords do not match
              </p>
            )}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          disabled={loading || !isFormValid}
          data-testid={isRegister ? 'register-submit' : 'login-submit'}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isRegister ? 'Creating account…' : 'Signing in…'}
            </>
          ) : (
            isRegister ? 'Start Free' : 'Sign In'
          )}
        </Button>

        {!isRegister && onForgotPassword && (
          <div className="text-center">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </button>
          </div>
        )}
      </form>

      <p className="mt-5 pt-4 border-t border-white/5 text-center text-xs text-muted-foreground">
        {isRegister
          ? 'No approval needed · Instant access · No real money required'
          : 'Secure access to your trading dashboard'}
      </p>
    </Card>
  );
}
